import React, { useState, useMemo, useEffect } from "react";
import WidgetName from "../WidgetName";
import BallotCard from "./BallotCard";
import { titleContainsText } from "../helpers";
import Typeahead from "../utils/Typeahead/Typeahead";
import "./race-card.scss";
import RaceCard from "./RaceCard";
import "./universal-widget.scss";
import ErrorBoundary from "../ErrorBoundary";
import Autocomplete from "../Autocomplete";
import { ReactComponent as ListIcon } from "../images/icon-list.svg";
import { ReactComponent as CloseIcon } from "../images/icon-close.svg";

const UniversalWidget = (props) => {
  const raceDisplay = props.widget.raceDisplay;
  const title = props.widget.title;
  const [generalInputValue, setGeneralInputValue] = useState("");
  const [countyInputValue, setCountyInputValue] = useState("");
  const [inputSource, setInputSource] = useState("");
  const [inputSourceFocused, setInputSourceFocused] = useState("");
  const [mouseHovererdElement, setMouseHoveredElement] = useState("");
  const [tooltipMessage, setTooltipMessage] = useState("");

  // oct 1st 2024
  const cutoffDate = 1727740800;
  const createdDate = props.widget && props.widget.createdAt ? new Date(props.widget.createdAt).getTime() / 1000 : null;
  const isAfterCutoffDate = createdDate >= cutoffDate;

  // Initialize expandedCards based on the raceDisplay mode
  const [expandedCardIndex, setExpandedCardIndex] = useState([]);

  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    setIsMobileDevice(window.innerWidth <= 1023);
  }, []);

  useEffect(() => {
    if (raceDisplay === "expand-all") {
      setExpandedCardIndex(props.races.map((_, index) => index)); // Expand all cards
    } else if (raceDisplay === "collapse-all") {
      setExpandedCardIndex([]); // Collapse all cards
    } else if (raceDisplay === "auto") {
      setExpandedCardIndex([0]); // Expand the first card by default
    }
  }, [raceDisplay, props.races]);

  const isElectionSlider = title && title.toLowerCase().includes('#slider');

  const isSingleCard = Array.isArray(props?.races) && props?.races.length === 1;
  const isDisplayCountySearch = props?.widget?.accountId === 'cmg-ajc';

  const handleGeneralInputChange = (input) => {
    setCountyInputValue(''); // Clear county filter input
    setInputSource('generalFilter');
    setGeneralInputValue(input);
  };

  const handleCountyInputChange = (input) => {
    setGeneralInputValue(''); // Clear general filter input
    setInputSource('countyFilter');
    setCountyInputValue(input);
  };

  const handleClearInput = () => {
    setGeneralInputValue('');
    setCountyInputValue('');
    setInputSource('');
  };

  const handleFocusChanged = (isFocused, inputType) => {
    if(isFocused) {
      setInputSourceFocused(inputType)
    } else {
      setInputSourceFocused('')
    }
  }

  const handleMouseEnter = (inputType) => {
    if(!isMobileDevice) {
      setMouseHoveredElement(inputType);
      if (inputType === 'countySearch' && isCountyDisabled) {
        setTooltipMessage("Clear search box to select a county.");
      } else if (inputType === 'generalSearch' && isGeneralDisabled) {
        setTooltipMessage("Clear County selection to search.");
      } else {
        setTooltipMessage("");
      }
    }
  };

  const handleMouseLeave = () => {
    if(!isMobileDevice) {
      setMouseHoveredElement("");
      setTooltipMessage("");
    }
  };

  const handleInputBlur = () => {
    if(isMobileDevice) {
      setMouseHoveredElement("");
      setTooltipMessage("");
    }
  };

  const handleInputFocus = (inputType) => {
    if(isMobileDevice) {
      setMouseHoveredElement(inputType);
      if (inputType === 'countySearch' && isCountyDisabled) {
        setTooltipMessage("Clear search box to select a county.");
      } else if (inputType === 'generalSearch' && isGeneralDisabled) {
        setTooltipMessage("Clear County selection to search.");
      } else {
        setTooltipMessage("");
      }
    }
  };

  const counties = (props?.races || [])
  .map(race => 
    race?.description && race.description.includes("#")
      ? race.description.split("#")[1]
          .split(",")
          .map(county => county.trim())
          .filter(county => county)
      : []
  )
  .flat()
  .filter((county, index, self) => county && self.indexOf(county) === index)
  .sort();

  const countyDropdownLabel = counties.map(county => ({
    label: county,
    value: []
  }));

  // Determine if inputs should be disabled
  const isGeneralDisabled = countyInputValue.length > 0;
  const isCountyDisabled = generalInputValue.length > 0;

  // Handle card clicks based on raceDisplay mode
  const handleCardClick = (index) => {
    if (raceDisplay === "collapse-all" || raceDisplay === "expand-all") {
      // Toggle individual cards without restriction
      setExpandedCardIndex((prevExpanded) =>
        prevExpanded.includes(index)
          ? prevExpanded.filter((i) => i !== index) // Collapse the card
          : [...prevExpanded, index] // Expand the card
      );
    } else if (raceDisplay === "auto") {
      // Only allow one card to expand at a time
      setExpandedCardIndex((prevExpanded) =>
        prevExpanded.includes(index) ? [] : [index]
      );
    }
  };

  // Check if a card is collapsed or expanded
  const isCardCollapsed = (index) => {
    if (raceDisplay === "collapse-all") {
      return !expandedCardIndex.includes(index); // Collapse all unless clicked
    } else if (raceDisplay === "expand-all") {
      return !expandedCardIndex.includes(index); // Expand all unless clicked
    } else if (raceDisplay === "auto") {
      return !expandedCardIndex.includes(index); // Auto logic: only one can be expanded
    }
    return true; // Default to collapsed
  };

  const filteredRaces = useMemo(() => {
    return props.races.filter((race) => {
      const matchesGeneralFilter = titleContainsText(generalInputValue, countyDropdownLabel, 'generalFilter')(race);
      const matchesCountyFilter = countyInputValue && titleContainsText(countyInputValue, countyDropdownLabel, 'countyFilter')(race);

      if (inputSource === 'countyFilter') {
        return matchesCountyFilter;
      } else if (inputSource === 'generalFilter') {
        return matchesGeneralFilter;
      } else {
        return true;
      }
    });
  }, [generalInputValue, countyInputValue, props.races, inputSource, countyDropdownLabel]);

  const buildSuggestions = (races) => {
    const suggestionSet = new Set();
  
    races.forEach((race) => {
      if (race.name){
        var cleanName = race.name.replace(/&quot;/g, '"');
        cleanName = cleanName.replace(/&amp;/g, '&');
        suggestionSet.add(cleanName);
      }
      if (race.name_override) suggestionSet.add(race.name_override);
      //if (race.id) suggestionSet.add(race.id);
      //if (race.raceSource) suggestionSet.add(race.raceSource);
      if (race.description) {
        const cleanDescription = race.description.split('#')[0];
        suggestionSet.add(cleanDescription);
      }
      if (!race.isRaceAnIssue) {
        const raceCandidates = race.candidates || [];
        raceCandidates.forEach((candidate) => {
          if (candidate.name){
            var cleanName = candidate.name.replace(/&quot;/g, '"');
            cleanName = cleanName.replace(/&amp;/g, '&');
            suggestionSet.add(cleanName);
          } 
          if (candidate.name_override) suggestionSet.add(candidate.name_override);
        });
      }
    });
  
    return Array.from(suggestionSet);
  };

  return (
    <div className={`universal ${isElectionSlider ? 'isElectionSlider' : ''} ${isSingleCard ? 'isSingleCard' : ''}`}>
      <div className="inputContainer">
        {props.widget.showSearchBar && (
          <div tabIndex={isMobileDevice ? 0 : undefined} className={`universalSearch ${inputSourceFocused === 'countyFilter' || isGeneralDisabled ? 'disabled' : ''}`}
          onMouseLeave={handleMouseLeave}
          onMouseEnter={() => handleMouseEnter('generalSearch')}
          onFocus={() => handleInputFocus('generalSearch')}
          onBlur={handleInputBlur}>
            { isCountyDisabled &&  tooltipMessage && mouseHovererdElement === 'countySearch' && (
            <div className="tooltip universal">
              {tooltipMessage}
            </div>
            )}
            <Autocomplete
              suggestions={buildSuggestions(props.races)}
              onUserInput={handleGeneralInputChange}
              clearInput={handleClearInput}
              focusChanged={handleFocusChanged}
              inputValue={generalInputValue}
              disabled={isGeneralDisabled}
            />
          </div>
        )}
        {props.widget.showSearchBar && isDisplayCountySearch && isAfterCutoffDate ? (
          <div className="separator"><span>Or</span></div>
        ) : ''}
        {props.widget.showSearchBar && isDisplayCountySearch && isAfterCutoffDate ? (
          <div tabIndex={isMobileDevice ? 0 : undefined} className={`countySearch ${inputSourceFocused === 'generalFilter' || isCountyDisabled ? 'disabled' : ''}`}
          onMouseLeave={handleMouseLeave}
          onMouseEnter={() => handleMouseEnter('countySearch')}
          onFocus={() => handleInputFocus('countySearch')}
          onBlur={handleInputBlur}>
            { isGeneralDisabled && tooltipMessage && mouseHovererdElement === 'generalSearch' && (
              <div className="tooltip county">
                {tooltipMessage}
              </div>
            )}
            <ListIcon className="list-icon" />
            <div className="typeahead-wrapper">
              <Typeahead
                id="universal-widget-dropdown"
                onChange={(input) => handleCountyInputChange(input[0] || '')}
                options={countyDropdownLabel}
                placeholder="Select a County"
                focusChanged={handleFocusChanged}
                selected={countyInputValue ? [countyInputValue] : []}
                disabled={isCountyDisabled}
              />
            </div>
            {countyInputValue ? <CloseIcon className="close-icon" onClick={handleClearInput} /> : ''}
          </div>
        ) : ''}
      </div>

      <WidgetName widget={props.widget} updated={props.updated} />

      {filteredRaces.map((race, index) => (
        <div key={index}>
          {race.raceType === "issue" ? (
            <ErrorBoundary message={process.env.REACT_APP_ERROR_MESSAGE}>
              <BallotCard
                widget={props.widget}
                ballot={race}
                onToggle={isSingleCard ? null : () => handleCardClick(index)}
                expanded={!isCardCollapsed(index)} 
              />
            </ErrorBoundary>
          ) : (
            <ErrorBoundary message={process.env.REACT_APP_ERROR_MESSAGE}>
              <RaceCard
                widget={props.widget}
                race={race}
                onToggle={isSingleCard ? null : () => handleCardClick(index)}
                expanded={!isCardCollapsed(index)} 
              />
            </ErrorBoundary>
          )}
        </div>
      ))}
    </div>
  );
};

export default UniversalWidget;
